import React, { FC, useEffect, useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { formatDateTime } from "utils/converSelectedDateToString";
import ButtonCircle from "shared/Button/ButtonCircle";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useData } from "data/data-provider";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import {
  CreateStripeVerification,
  CreateVouchVerification,
} from "api/stripe/stripe";
import { CanopyPayload } from "api/types";
import { CreateCanopyVerification } from "api/Canopy/canopy";

export interface PayPageProps {
  className?: string;
}

const PayPage: FC<PayPageProps> = ({ className = "" }) => {
  const {
    invoice,
    selectedCar,
    pickupLocation,
    searchedDropoffLocation,
    searchedPickupLoction,
    dropoffLocationType,
    dropOffLocation,
    startDate,
    endDate,
    ourCompany,
    bookingdetails,
  } = useData();
  const navigate = useNavigate();
  const [canopyLoading, setCanopyLoading] = useState(false);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [canopyLink, setCanopyLink] = useState<any>();
  const [identityLink, setIdentityLink] = useState<any>();
  const [loading, setLoading] = useState(false);

  const handleCanopyVerificationButtonClick = async () => {
    setCanopyLoading(true);
    const payload: CanopyPayload = {
      user_id: bookingdetails?.customer?.id ?? "",
    };

    const IgnitionResponse = (await CreateCanopyVerification(payload)) as {
      data?: { insurance_verification_link?: string };
      error?: string;
    };

    if (
      IgnitionResponse.data &&
      IgnitionResponse.data.insurance_verification_link
    ) {
      const ignitionUrl = IgnitionResponse.data.insurance_verification_link;
      setCanopyLink(ignitionUrl);
      setCanopyLoading(false);

      // try {
      //   await copy(ignitionUrl);
      //   toast.success("Insurance verification link copied to clipboard!");
      // } catch (error) {
      //   toast.error("Failed to copy link. Please try again.");
      // }

      return;
    } else {
      console.error(
        "Failed to retrieve insurance verification link:",
        IgnitionResponse.error
      );
      toast.error("Verification failed. Please try again later.");
      setCanopyLoading(false);
    }
  };

  const handlePrimaryDriverVerificationButtonClick = async () => {
    setIdentityLoading(true);
    const payload = { customer_id: bookingdetails?.customer?.id ?? "" };
    if (ourCompany?.default_identity === "vouched") {
      const stripeSessionResponse = (await CreateVouchVerification(
        payload
      )) as {
        data?: { session_url?: string };
        error?: string;
      };
      if (
        stripeSessionResponse.data &&
        stripeSessionResponse.data.session_url
      ) {
        const ignitionUrl = stripeSessionResponse.data.session_url;
        setIdentityLink(ignitionUrl);
        setIdentityLoading(false);
        return;
      } else {
        // Handle the case where data or session_url is undefined
        console.error(
          "Failed to retrieve session URL:",
          stripeSessionResponse.error
        );
        toast.error("Verification failed. Please try again later.");
        setIdentityLoading(false);
      }
    } else {
      const stripeSessionResponse = (await CreateStripeVerification(
        payload
      )) as {
        data?: { session_url?: string };
        error?: string;
      };
      if (
        stripeSessionResponse.data &&
        stripeSessionResponse.data.session_url
      ) {
        const ignitionUrl = stripeSessionResponse.data.session_url;
        setIdentityLink(ignitionUrl);
        setIdentityLoading(false);
        return;
      } else {
        // Handle the case where data or session_url is undefined
        console.error(
          "Failed to retrieve session URL:",
          stripeSessionResponse.error
        );
        toast.error("Verification failed. Please try again later.");
        setIdentityLoading(false);
      }
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!pickupLocation) {
      navigate("/");
    }
  }, [pickupLocation]);

  const renderContent = () => {
    return (
      <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8 px-0 sm:p-6 xl:p-8">
        <h2 className="text-3xl lg:text-4xl font-semibold">
          Congratulations 🎉
        </h2>

        <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
        <div className="space-y-6">
          <div className="flex flex-col justify-between sm:flex-row sm:items-center">
            <div>
              <h3 className="text-2xl font-semibold">
                Your booking request has been confirmed. You will be notified
                soon.
              </h3>
              <p className="text-lg mt-3 font-semibold">{selectedCar?.name}</p>
              <p className="text-md  text-gray-500">
                {selectedCar?.plate_number}
              </p>
            </div>

            <div className="flex-shrink-0 w-full sm:w-40">
              <div className=" aspect-w-4 aspect-h-3 sm:aspect-h-4 rounded-2xl overflow-hidden">
                <img
                  alt=""
                  className="absolute inset-0 object-cover w-full"
                  sizes="200px"
                  src={
                    (selectedCar?.fleet_photos?.length ?? 0) > 0
                      ? `https://fleet-management-images-upload-be.s3.amazonaws.com/${selectedCar?.fleet_photos[0].photo_url}`
                      : "https://tenant.fleetwire.io/images/vehicle-placeholder.jpg"
                  }
                />
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-2xl font-semibold">Location</h3>
            <div className="mt-6 border border-neutral-200 dark:border-neutral-700 rounded-3xl flex flex-col sm:flex-row divide-y sm:divide-x sm:divide-y-0 divide-neutral-200 dark:divide-neutral-700 overflow-hidden z-10">
              <div className="flex flex-col flex-1 p-5">
                <span className="text-sm text-neutral-400">
                  Pick up Location
                </span>
                <span className="mt-1.5 text-lg font-semibold">
                  {searchedPickupLoction
                    ? searchedPickupLoction
                    : pickupLocation?.name}
                </span>
              </div>

              <div className="flex flex-col flex-1 p-5">
                <span className="text-sm text-neutral-400">
                  Drop off Location
                </span>
                <span className="mt-1.5 text-lg font-semibold">
                  <span className="line-clamp-1">
                    {dropoffLocationType === false &&
                    searchedDropoffLocation.length > 0
                      ? searchedDropoffLocation
                      : dropOffLocation?.name
                      ? dropOffLocation?.name
                      : dropoffLocationType === true &&
                        searchedPickupLoction.length > 0
                      ? searchedPickupLoction
                      : pickupLocation?.name}
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-2xl font-semibold">Date & Time</h3>
            <div className="mt-6 border border-neutral-200 dark:border-neutral-700 rounded-3xl flex flex-col sm:flex-row divide-y sm:divide-x sm:divide-y-0 divide-neutral-200 dark:divide-neutral-700 overflow-hidden z-10">
              <div className="flex flex-col flex-1 p-5">
                <span className="text-sm text-neutral-400">
                  Pick up Date & Time
                </span>
                <span className="mt-1.5 text-lg font-semibold">
                  {moment(startDate).format("ddd, D MMM YYYY")}{" "}
                  {moment(startDate).format("hh:mm A")}
                </span>
              </div>

              <div className="flex flex-col flex-1 p-5">
                <span className="text-sm text-neutral-400">
                  Drop off Date & Time
                </span>
                <span className="mt-1.5 text-lg font-semibold">
                  <span className="line-clamp-1">
                    {moment(endDate).format("ddd, D MMM YYYY")}{" "}
                    {moment(endDate).format("hh:mm A")}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex flex-col space-y-4">
            <div className="w-full  mx-auto ">
              <h2 className="text-xl font-medium text-gray-900 dark:text-gray-100">
                Rental Bill
              </h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Prices may change depending on the length of the rental and the
                price of your rental car.
              </p>
              {invoice && Object.keys(invoice).length > 0 && (
                <>
                  <div className="mt-4">
                    <div className="pb-4">
                      <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                        Trip Price
                      </span>
                    </div>
                    <div className="flex justify-between ">
                      <span>{selectedCar?.name}</span>
                      <span className="flex flex-col items-end ">
                        <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                          ${invoice.car_total}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          ${invoice?.avg_car_price_per_day + " x "}
                          {invoice.trip_days + "-Days  "}
                        </span>
                      </span>
                    </div>
                  </div>

                  {invoice?.location_total !== "0.00" && (
                    <div className="mt-4">
                      <div className="pb-4">
                        <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                          Location Total
                        </span>
                      </div>
                      {invoice?.breakdown?.locations?.map((location: any) => (
                        <>
                          <div className="flex justify-between ">
                            <span>{location.name}</span>
                            <div className="flex flex-col items-end">
                              <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                                ${location.total}
                              </span>
                            </div>
                          </div>
                        </>
                      ))}
                    </div>
                  )}

                  {
                    <div className="mt-4">
                      <div className="pb-4">
                        <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                          Insurance Total
                        </span>
                      </div>
                      {invoice?.breakdown?.insurance?.map((insurance: any) => (
                        <>
                          <div className="flex justify-between ">
                            <span>{insurance.name}</span>
                            <div className="flex flex-col items-end">
                              <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                                ${insurance.total}
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {insurance.rate}
                              </span>
                            </div>
                          </div>
                        </>
                      ))}
                    </div>
                  }

                  {invoice?.extras_total !== "0.00" && (
                    <div className="mt-4">
                      <div className="pb-4">
                        <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                          Trip Extras
                        </span>
                      </div>
                      {invoice?.breakdown?.extras?.map((extra: any) => (
                        <>
                          <div className="flex justify-between ">
                            <span>{extra.name}</span>
                            <div className="flex flex-col items-end">
                              <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                                ${extra.total}
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                ${extra.rate}
                              </span>
                            </div>
                          </div>
                        </>
                      ))}
                    </div>
                  )}

                  {invoice?.booking_fee !== "0.00" &&
                    invoice?.booking_fee !== undefined && (
                      <div className="mt-4">
                        <div className="pb-4">
                          <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                            Booking Fee
                          </span>
                        </div>
                        {invoice?.breakdown?.booking_fee?.map(
                          (bookingFee: any) => (
                            <>
                              <div className="flex justify-between ">
                                <span>
                                  {" "}
                                  {bookingFee?.description
                                    ? bookingFee?.description
                                    : bookingFee?.name}
                                </span>
                                <div className="flex flex-col items-end">
                                  <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                                    ${bookingFee?.total}
                                  </span>
                                  <span>
                                    {bookingFee?.rate.includes("%")
                                      ? bookingFee?.rate
                                      : `$${bookingFee?.rate}`}
                                  </span>
                                </div>
                              </div>
                            </>
                          )
                        )}
                      </div>
                    )}

                  {invoice?.tax !== "0.00" && (
                    <div className="mt-4">
                      <div className="pb-4">
                        <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                          Tax & Fee
                        </span>
                      </div>
                      {invoice?.breakdown?.taxes?.map((taxes: any) => (
                        <>
                          <div className="flex justify-between ">
                            <span>
                              {" "}
                              {taxes.description
                                ? taxes.description
                                : taxes.name}
                            </span>
                            <div className="flex flex-col items-end">
                              <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                                ${taxes.total}
                              </span>
                              <span>
                                {taxes.rate.includes("%")
                                  ? taxes.rate
                                  : `$${taxes.rate}`}
                              </span>
                            </div>
                          </div>
                        </>
                      ))}
                    </div>
                  )}

                  {invoice?.discount !== "0.00" && (
                    <div className="mt-4">
                      <div className="flex justify-between ">
                        <span className="text-lg font-normal text-gray-700 dark:text-gray-300">
                          Discount
                        </span>
                        <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                          ${invoice.discount}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="mt-8 border-t border-border-[#E5E7EB] dark:border-gray-700 pt-4">
                    <div className="flex justify-between ">
                      <div>
                        <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                          Total Rental Price
                        </span>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Overall price rental
                        </p>
                      </div>
                      <div>
                        <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                          ${invoice.total_price_without_security}
                        </span>
                      </div>
                    </div>
                  </div>

                  {invoice.security_deposit_total !== "0.00" && (
                    <div className="mt-4">
                      <div className="flex justify-between ">
                        <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                          Refundable Deposit
                        </span>
                        <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                          ${invoice.security_deposit_total}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-5">
          <ButtonPrimary href="/">Go to Home</ButtonPrimary>
        </div>
      </div>
    );
  };

  return (
    <div className={`nc-PayPage ${className}`} data-nc-id="PayPage">
      <>
        {loading ? (
          <div className="flex justify-center items-center col-span-full py-96">
            <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
          </div>
        ) : (
          <div className={`nc-PayPage ${className}`} data-nc-id="PayPage">
            {!identityLink &&
              bookingdetails &&
              bookingdetails?.customer?.identity_verified !== true && (
                <>
                  <div className="min-h-22 bg-[#FF757587] flex items-center p-4">
                    <div className="flex flex-row w-full items-center justify-between max-w-6xl mx-auto">
                      <span className="text-[#FF0000] font-medium text-[14px] md:text-2xl">
                        Verify your{" "}
                        <span className="font-extrabold">identity</span> to
                        confirm your booking
                      </span>
                      <ButtonPrimary
                        onClick={handlePrimaryDriverVerificationButtonClick}
                        className="!bg-[#FF0000] min-w-44 !hover:bg-[#FF0000] text-white text-[12px] font-bold py-2 px-2 rounded-xl whitespace-nowrap"
                      >
                        {identityLoading ? (
                          <div className="flex justify-center items-center col-span-full">
                            <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                          </div>
                        ) : (
                          "Create Verification Link"
                        )}
                      </ButtonPrimary>
                    </div>
                  </div>
                </>
              )}
            {identityLink && (
              <div className="min-h-22 bg-[#FF757587] flex items-center p-4">
                <div className="flex flex-row w-full items-center justify-between max-w-6xl mx-auto">
                  <span className="text-white font-medium text-[14px] md:text-2xl break-words truncate">
                    {identityLink}
                  </span>
                  <div className="space-x-4 flex-col md:flex-row space-y-4">
                    {/* <button className="ttnc-ButtonPrimary disabled:bg-opacity-70 bg-none border text-sm border-[#FF0000] text-[#FF0000] font-bold py-3 px-4 sm:px-6 rounded-xl">
                    Purchase Insurance
                  </button> */}
                    <ButtonPrimary
                      onClick={() => {
                        navigator.clipboard.writeText(identityLink);
                        toast.success("Link Copied");
                      }}
                      className="!bg-[#FF0000] min-w-20 ml-2 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                    >
                      Copy
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            )}
            {!canopyLink &&
              bookingdetails &&
              bookingdetails?.customer?.insurance_verified !== true && (
                <div className="min-h-22 bg-[#FF757587] flex items-center p-4 mt-4">
                  <div className="flex flex-row w-full items-center justify-between max-w-6xl mx-auto">
                    <span className="text-[#FF0000] font-medium text-[14px] md:text-2xl ">
                      Verify your{" "}
                      <span className="font-extrabold">insurance</span> to
                      confirm your booking
                    </span>
                    <div className="space-x-4 flex-col md:flex-row space-y-4">
                      <ButtonPrimary
                        onClick={handleCanopyVerificationButtonClick}
                        className="!bg-[#FF0000] min-w-44 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                      >
                        {canopyLoading ? (
                          <div className="flex justify-center items-center col-span-full">
                            <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-gray-300"></div>
                          </div>
                        ) : (
                          "Create Verification Link"
                        )}
                      </ButtonPrimary>
                    </div>
                  </div>
                </div>
              )}
            {canopyLink && (
              <div className="min-h-22 bg-[#FF757587] flex items-center p-4 mt-4">
                <div className="flex flex-row w-full items-center justify-between max-w-6xl mx-auto">
                  <span className="text-white font-medium text-[14px] md:text-2xl break-words truncate">
                    {canopyLink}
                  </span>
                  <div className="space-x-4 flex-col md:flex-row space-y-4">
                    {/* <button className="ttnc-ButtonPrimary disabled:bg-opacity-70 bg-none border text-sm border-[#FF0000] text-[#FF0000] font-bold py-3 px-4 sm:px-6 rounded-xl">
                    Purchase Insurance
                  </button> */}
                    <ButtonPrimary
                      onClick={() => {
                        navigator.clipboard.writeText(canopyLink);
                        toast.success("Link Copied");
                      }}
                      className="!bg-[#FF0000] min-w-20 ml-2 !hover:bg-[#FF0000] text-white text-[12px] font-bold rounded-xl px-4 py-3 whitespace-nowrap"
                    >
                      Copy
                    </ButtonPrimary>
                  </div>
                </div>
              </div>
            )}

            <main className="container mt-11 mb-24 lg:mb-32 ">
              <div className="max-w-6xl mx-auto">{renderContent()}</div>
            </main>
          </div>
        )}
      </>
    </div>
  );
};

export default PayPage;
